/**
 * useCompactQueryModel — derives everything the Compact VQE popover body needs
 * to render its sections, off the SAME live edit context / Resolver service as
 * the full VQE. No parallel parse: we read the service's parsed buffer (the one
 * the commit pipeline writes through), find the cursor statement by id, and
 * compute its clause outline + `QueryStructure` + degrade verdict.
 *
 * Recomputes whenever the buffer text changes (`query` flows back in after each
 * committed edit), so the sections, summary, live preview, and degrade banner
 * always reflect the latest buffer — the source of truth.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3.
 */

import { useMemo } from 'react';

import type { ParseOptions } from '../../../sql-grammar/model';
import type { StatementInfo } from '../../../sql-grammar/model';
import type { QueryStructure } from '../../../sql-resolver/types/queryStructure';
import type { VqeEditContext } from '../edit/editContext';
import { extractStructureForContext } from '../edit/editContext';
import type { RenderMode } from '../types';
import { classifyCompactDegrade, type CompactDegradeResult } from './compactDegrade';
import type { OffsetSpan } from './compactLivePreview';
import { extractCtes, type CteOutline } from '../render/select/extractCtes';
import { extractSelectOutline, type SelectOutline } from '../render/select/extractSelectOutline';
import { extractUpdateOutline, type UpdateOutline } from '../render/update/extractUpdateOutline';
import { extractDeleteOutline, type DeleteOutline } from '../render/delete/extractDeleteOutline';
import { extractInsertOutline } from '../render/insert/extractInsertOutline';

export interface CompactQueryModel {
  /** Full live buffer text (statements section components slice from this). */
  readonly bufferText: string;
  /** The cursor statement's offset span, or null when unresolved. */
  readonly span: OffsetSpan | null;
  /** The cursor statement (carries the SqlRange the section outline needs). */
  readonly statement: StatementInfo | null;
  /** Token-aware SELECT clause outline for the cursor statement. */
  readonly outline: SelectOutline | null;
  /**
   * WITH-clause outline for SELECT statements — feeds the compact WITH strip
   * (chips + drill) and the FROM rows' CTE-ref badge. Null for every other
   * mode: a WITH on UPDATE/DELETE still degrades the compact popover, so there
   * is no strip to feed there.
   */
  readonly cteOutline: CteOutline | null;
  /** UPDATE clause outline — set only when the cursor statement is an UPDATE. */
  readonly updateOutline: UpdateOutline | null;
  /** DELETE clause outline — set only when the cursor statement is a DELETE. */
  readonly deleteOutline: DeleteOutline | null;
  /** Live structured form (drives summary + degrade detection). */
  readonly structure: QueryStructure | null;
  /** Whether to degrade to a summary + Open-full call-to-action. */
  readonly degrade: CompactDegradeResult;
}

const EMPTY: CompactQueryModel = {
  bufferText: '',
  span: null,
  statement: null,
  outline: null,
  cteOutline: null,
  updateOutline: null,
  deleteOutline: null,
  structure: null,
  degrade: { degraded: false },
};

export function useCompactQueryModel(
  editContext: VqeEditContext | null,
  mode: RenderMode | null,
  options: ParseOptions,
): CompactQueryModel {
  return useMemo<CompactQueryModel>(() => {
    if (!editContext) return { ...EMPTY, degrade: classifyCompactDegrade(mode, null) };

    const buffer = editContext.service.getParsedBuffer(editContext.documentId);
    const bufferText = buffer?.parsedDocument.source ?? '';
    const statement =
      buffer?.documentStructure.statements.find((s) => s.id === editContext.statementId) ??
      // Drilled / set-op-branch contexts carry a synthetic StatementInfo whose id
      // (`<parent>.sub.START-END`) is NOT in documentStructure.statements. Fall
      // back to it so the span + outline resolve off its range (the base context
      // still resolves by id; only synthetic contexts use this fallback).
      editContext.statementInfo ??
      null;

    let structure: QueryStructure | null = null;
    try {
      structure = extractStructureForContext(editContext);
    } catch {
      structure = null;
    }

    const span: OffsetSpan | null = statement
      ? { start: statement.range.startOffset, end: statement.range.endOffset }
      : null;
    const outline =
      statement && mode !== 'update' && mode !== 'delete'
        ? extractSelectOutline(bufferText, statement.range, options)
        : null;
    // WITH clause — SELECT only (a WITH on UPDATE/DELETE still degrades, so the
    // strip would have nothing to mount into). Same offsets `CteCard`'s full-VQE
    // drill consumes, so the compact drill lands on the identical body range.
    const cteOutline =
      statement && mode === 'select' ? extractCtes(bufferText, statement.range, options) : null;
    const updateOutline =
      statement && mode === 'update'
        ? extractUpdateOutline(bufferText, statement.range, options)
        : null;
    const deleteOutline =
      statement && mode === 'delete'
        ? extractDeleteOutline(bufferText, statement.range, options)
        : null;
    // Only computed for INSERT — feeds `buildDegradeDetail`'s row-count/target/
    // columns/upsert facts (same token-walker InsertRenderer already uses in
    // full mode, so compact and full describe the same INSERT identically).
    const insertOutline =
      statement && mode === 'insert'
        ? extractInsertOutline(bufferText, statement.range, options)
        : null;

    return {
      bufferText,
      span,
      statement,
      outline,
      cteOutline,
      updateOutline,
      deleteOutline,
      structure,
      degrade: classifyCompactDegrade(mode, structure, options.engine, insertOutline),
    };
    // editContext identity changes on every service-relevant update; bufferText
    // recompute is driven by the parent passing a fresh edit context after each
    // committed edit (the buffer is the source of truth).
  }, [editContext, mode, options]);
}
